import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import Select from 'react-select';
import AppContext from 'Utils/AppContext';
import { getRulesListOptions } from 'DataDiscovery/Utils';
import { buildDiscoveryRequest, startDiscovery } from 'DataDiscovery/discoverApi';
import { createDataSource } from 'DataSources/datasourcesApi';
import { ensureConnectionTested } from 'Utils/connectionGate';
import {
  buildFileNameFilter,
  folderLeafName,
  trimTrailingSlash,
} from './folderPathUtils';

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

// FileNodeType (profile scan) → typed DatasourceType, when a typed source keeps full
// downstream capability (typed runs are maskable; generic-run results degrade for
// masking). Documents and anything unmapped fall back to FILE_GENERIC_FOLDER.
const NODE_TYPE_TO_DATASOURCE_TYPE = {
  CSV: 'FILE_DELIMITED_FOLDER',
  TSV: 'FILE_DELIMITED_FOLDER',
  PARQUET: 'FILE_PARQUET_FOLDER',
  JSON: 'FILE_JSON_FOLDER',
  XML: 'FILE_XML_FOLDER',
  EXCEL: 'FILE_EXCEL_FOLDER',
  HL7: 'FILE_HL7_FOLDER',
  EDI: 'FILE_EDI_FOLDER',
};

/**
 * Homogeneous non-document file set → its typed DatasourceType; anything mixed,
 * document-only, or unknown → FILE_GENERIC_FOLDER.
 */
export function deriveDatasourceType(files) {
  const mapped = new Set(
    (files || []).map((f) => NODE_TYPE_TO_DATASOURCE_TYPE[f.type] || 'FILE_GENERIC_FOLDER')
  );
  if (mapped.size === 1) return [...mapped][0];
  return 'FILE_GENERIC_FOLDER';
}

/**
 * Existing-source match for the exact same discovery scope: rootPath + type +
 * effective recurse + effective inclusion filter + effective EXCLUSION filter. Any other
 * overlap (same path, different scope) intentionally creates a new source.
 *
 * <p>The exclusion must be part of the identity: reusing a source whose exclusion differs
 * would silently run a different scope than the one the user just asked for. This dialog
 * never sets an exclusion itself, so in practice it reuses only exclusion-free sources —
 * the conservative direction, and deliberate.
 */
export function findReusableSource(
  connections,
  { rootPath, datasourceType, recurse, fileNameFilter, fileNameExcludeFilter },
) {
  const wantPath = trimTrailingSlash(rootPath);
  // '*' and '' both mean "not set" for either filter, so normalise before comparing.
  const normFilter = (v) => {
    const t = (v || '').trim();
    return t === '*' ? '' : t;
  };
  const wantFilter = normFilter(fileNameFilter);
  const wantExclude = normFilter(fileNameExcludeFilter);
  return (connections || []).find((c) => {
    if (c.datasourceType !== datasourceType) return false;
    if (trimTrailingSlash(c.rootPath || '') !== wantPath) return false;
    const cRecurse = c.recurse !== false;
    if (cRecurse !== (recurse !== false)) return false;
    if (normFilter(c.fileNameFilter) !== wantFilter) return false;
    return normFilter(c.fileNameExcludeFilter) === wantExclude;
  }) || null;
}

/**
 * Create-and-discover dialog for the metadata folder drill-down page. Two modes:
 * 'folder' (whole folder, recurse per checkbox) and 'selection' (exact file names →
 * anchored regex filter, recurse always false so same-named files in subfolders
 * don't leak in). Either way a NEW data source scoped to the folder is created
 * (credentials inherited server-side via parentDataSourceId), unless an existing
 * source already has the identical scope — then it's reused.
 */
const FolderDiscoverDialog = ({
  show,
  onHide,
  mode,               // 'folder' | 'selection'
  folderPath,
  recurse,            // folder mode only
  selectedFiles,      // selection mode only: [{name, path, type}]
  folderFiles,        // folder mode: all files in the folder (for type derivation)
  parentSource,       // UI source object: {id, name, vfsScheme}
}) => {
  const {
    connections, rulesList, loadConnectionsHandler, loadSearchStatusesHandler,
    markDiscoveryStarted, addOptimisticRun, setNotification,
  } = useContext(AppContext);
  const history = useHistory();

  const isSelection = mode === 'selection';
  const datasourceType = useMemo(
    () => deriveDatasourceType(isSelection ? (selectedFiles || []) : (folderFiles || [])),
    [isSelection, selectedFiles, folderFiles]
  );
  const fileNameFilter = isSelection
    ? buildFileNameFilter((selectedFiles || []).map((f) => f.name))
    : '';
  const effectiveRecurse = isSelection ? false : recurse !== false;
  // The exclusion a new child would be created with (inherited from the parent). It is part
  // of the child's SCOPE, so it has to be part of the reuse check too — otherwise the
  // coverage rule would never match the child it created a moment ago and would keep
  // minting duplicates.
  const inheritedExclude = (() => {
    const v = parentSource && parentSource.fileNameExcludeFilter;
    return v && v.trim() && v.trim() !== '*' ? v.trim() : '';
  })();

  const existing = useMemo(() => findReusableSource(connections, {
    rootPath: folderPath,
    datasourceType,
    recurse: effectiveRecurse,
    fileNameFilter,
    fileNameExcludeFilter: inheritedExclude,
  }), [connections, folderPath, datasourceType, effectiveRecurse, fileNameFilter,
    inheritedExclude]);

  const defaultDsName = useMemo(() => {
    if (existing) return existing.name || '';
    const leaf = folderLeafName(folderPath) || 'folder';
    const base = parentSource && parentSource.name ? `${parentSource.name}.${leaf}` : leaf;
    return isSelection ? `${base}.selection` : base;
  }, [existing, folderPath, parentSource, isSelection]);

  const rulesListOptions = useMemo(() => getRulesListOptions(rulesList || []), [rulesList]);
  const defaultRuleOption = useMemo(() => {
    const found = rulesListOptions.find((o) => o.value && o.value.id === 'DefaultSampling-3pct');
    return found || rulesListOptions[0] || null;
  }, [rulesListOptions]);

  const [dsName, setDsName] = useState('');
  const [discoverName, setDiscoverName] = useState('');
  const [selectedRule, setSelectedRule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show) {
      setDsName(defaultDsName);
      setDiscoverName(`${defaultDsName} ${todayStr()}`);
      setSelectedRule(defaultRuleOption);
      setError(null);
      setLoading(false);
    }
  }, [show, defaultDsName, defaultRuleOption]);

  const scopeSummary = isSelection
    ? `${(selectedFiles || []).length} selected file${(selectedFiles || []).length === 1 ? '' : 's'} in this folder`
    : effectiveRecurse
      ? 'Entire folder including sub folders'
      : 'Entire folder — this level only';

  const handleStart = async () => {
    if (!discoverName.trim()) {
      setError('Discovery name is required.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let sourceId;
      let sourceName;
      if (existing) {
        sourceId = existing.id;
        sourceName = existing.name;
      } else {
        if (!dsName.trim()) {
          setError('Data source name is required.');
          setLoading(false);
          return;
        }
        const config = {
          rootPath: trimTrailingSlash(folderPath),
          recurse: effectiveRecurse,
        };
        if (fileNameFilter) config.fileNameFilter = fileNameFilter;
        // Inherit the parent's EXCLUSION. Without it a child created from this dialog scans
        // unfiltered, so a parent that excludes build output spawns children that download
        // exactly what the parent refused. The parent's inclusion filter is deliberately NOT
        // inherited: `fileNameFilter` here is this dialog's own selection alternation, and
        // the parent's would either fight it or (in selection mode) scope the child to
        // nothing.
        //
        // Note on folder PATH patterns: a child is rooted deeper than its parent, and path
        // patterns match root-relative paths. A `**/`-prefixed pattern (what we seed and
        // document) is root-agnostic and behaves identically; a parent-root-anchored one
        // like `build/**` means something different under the child's root.
        if (inheritedExclude) {
          config.fileNameExcludeFilter = inheritedExclude;
        }
        const dto = {
          name: dsName.trim(),
          datasourceType,
          engine: ((parentSource && parentSource.vfsScheme) || 'LOCAL').toLowerCase(),
          vfsScheme: (parentSource && parentSource.vfsScheme) || 'LOCAL',
          config,
          // Server-side credential inheritance (DataSourceServiceImpl
          // .inheritParentVfsCredentialsIfRequested) — no creds pass through the UI.
          parentDataSourceId: parentSource && parentSource.id != null ? Number(parentSource.id) : null,
        };
        const newDs = await createDataSource(dto);
        sourceId = newDs.id;
        sourceName = dsName.trim();
      }

      // Verify (or establish) a successful connection test before starting; on failure
      // the helper routes the user to the source editor — close the dialog behind them.
      const gate = await ensureConnectionTested(sourceId, {
        history,
        setNotification,
        name: sourceName,
      });
      if (!gate.ok) {
        setLoading(false);
        onHide();
        return;
      }

      const rule = (selectedRule && selectedRule.value) || (rulesList && rulesList[0]) || null;
      const req = buildDiscoveryRequest({ id: sourceId }, rule, discoverName.trim());
      await startDiscovery(req);
      markDiscoveryStarted(sourceId);
      if (typeof addOptimisticRun === 'function') {
        addOptimisticRun({
          searchName: discoverName.trim(),
          connection: sourceName,
          type: ((parentSource && parentSource.vfsScheme) || 'LOCAL').toLowerCase(),
          dataSourceId: sourceId,
          sampleRules: rule?.name,
        });
      }
      onHide();
      setNotification({
        header: 'Discovery Started',
        body: `Discovery started for "${sourceName || ''}".`,
      });
      await Promise.all([loadConnectionsHandler(), loadSearchStatusesHandler()]);
    } catch (e) {
      setError(e && e.message ? e.message : 'Failed to start discovery.');
      setLoading(false);
    }
  };

  const isCreating = !existing;
  const canStart = discoverName.trim() !== '' && (isCreating ? dsName.trim() !== '' : true);

  return (
    <Modal show={show} onHide={onHide} backdrop='static' keyboard={!loading}>
      <Modal.Header closeButton={!loading}>
        <Modal.Title style={{ fontFamily: 'Rubik', fontSize: 18 }}>
          Discover folder
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant='danger' style={{ fontFamily: 'Rubik', fontSize: 14 }}>
            {error}
          </Alert>
        )}
        <div style={{ fontFamily: 'monospace', fontSize: 13, marginBottom: 4, wordBreak: 'break-all' }}>
          {folderPath}
        </div>
        <div className='text-muted' style={{ fontSize: 13, marginBottom: 12 }} data-testid='scope-summary'>
          {scopeSummary}
          {' · '}
          {datasourceType === 'FILE_GENERIC_FOLDER' ? 'Folder Search'
            : datasourceType === 'FILE_HL7_FOLDER' ? 'HL7'
            : datasourceType === 'FILE_EDI_FOLDER' ? 'EDI'
            : datasourceType.replace('FILE_', '').replace('_FOLDER', '').toLowerCase()} source
        </div>
        {existing && (
          <Alert variant='info' style={{ fontFamily: 'Rubik', fontSize: 13 }} data-testid='reuse-note'>
            Using existing data source “{existing.name}” — it already covers exactly this scope.
          </Alert>
        )}
        <Form.Group>
          <Form.Label style={{ fontFamily: 'Rubik', fontWeight: 600, fontSize: 14 }}>
            Data Source Name
          </Form.Label>
          <Form.Control
            type='text'
            value={dsName}
            disabled={!isCreating || loading}
            onChange={(e) => setDsName(e.target.value)}
            placeholder='Data source name'
          />
          {isCreating && (
            <Form.Text className='text-muted'>
              A new data source scoped to this folder will be created. Credentials are
              inherited from the parent source.
            </Form.Text>
          )}
        </Form.Group>
        <Form.Group className='mt-3'>
          <Form.Label style={{ fontFamily: 'Rubik', fontWeight: 600, fontSize: 14 }}>
            Discovery Name
          </Form.Label>
          <Form.Control
            type='text'
            value={discoverName}
            disabled={loading}
            onChange={(e) => setDiscoverName(e.target.value)}
            placeholder='Discovery run name'
          />
        </Form.Group>
        <Form.Group className='mt-3'>
          <Form.Label style={{ fontFamily: 'Rubik', fontWeight: 600, fontSize: 14 }}>
            Sampling Rules
          </Form.Label>
          <Select
            options={rulesListOptions}
            value={selectedRule}
            onChange={(opt) => setSelectedRule(opt)}
            isDisabled={loading}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='outline-secondary' onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={handleStart}
          disabled={loading || !canStart}
        >
          {loading ? (
            <>
              <Spinner animation='border' size='sm' className='mr-2' />
              Starting…
            </>
          ) : (
            'Start'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FolderDiscoverDialog;
